/**
 * The made-up world the preview pages draw from, so they tell one story. Everything
 * here is invented, and every page that shows it carries a PreviewBanner or a
 * DemoPill (CLAUDE.md, "Demo data").
 *
 * The leads and casinos match the local demo seed (backend/deploy/local/*_demo.sql),
 * so a preview page can link to the real list filtered by email or brand, for
 * example `/leads?q=maria.invest@example.com` or `/casinos?q=Royal Fortune`.
 * Mock ids are not real ids: never build `/leads/<mock id>` links from them.
 */

export type MockStage = 'new' | 'configuring' | 'paid' | 'live';

export type MockStaff = { id: string; name: string; email: string; role: 'member' | 'admin'; telegram: string };

export const MOCK_STAFF: MockStaff[] = [
  { id: 'staff-sara', name: 'Sara Ionescu', email: 'sara@prisma.example', role: 'admin', telegram: 'sara_cs' },
  { id: 'staff-mihai', name: 'Mihai Pop', email: 'mihai@prisma.example', role: 'member', telegram: 'mihai_sales' },
];

export type MockLead = {
  id: string;
  email: string;
  name: string;
  company: string;
  country: string; // ISO 3166-1 alpha-3, as the real API sends it
  telegram?: string;
  stage: MockStage;
};

export const MOCK_LEADS: MockLead[] = [
  { id: 'lead-maria', email: 'maria.invest@example.com', name: 'Maria Gomez', company: 'MG Holdings', country: 'PER', telegram: 'maria_invest', stage: 'new' },
  { id: 'lead-alex', email: 'cryptoalex@example.com', name: 'Alex Novak', company: '', country: 'CZE', telegram: 'cryptoalex88', stage: 'new' },
  { id: 'lead-laura', email: 'info@gamingventures.example', name: 'Laura Chen', company: 'Gaming Ventures', country: 'MEX', telegram: 'gamingventures', stage: 'configuring' },
  { id: 'lead-jonas', email: 'partners@luckypartners.example', name: 'Jonas Berg', company: 'Lucky Partners', country: 'BRA', telegram: 'luckypartners', stage: 'new' },
  { id: 'lead-dan', email: 'dan@example.com', name: 'Dan Marin', company: 'Lucky Clover', country: 'ROU', telegram: 'dan_lucky', stage: 'live' },
  { id: 'lead-carla', email: 'carla@example.com', name: 'Carla Souza', company: 'Aura Bet', country: 'BRA', telegram: 'carla_casino', stage: 'paid' },
  { id: 'lead-royal', email: 'owner@royalfortune.example', name: 'Victor Hale', company: 'Royal Fortune', country: 'MLT', stage: 'live' },
];

export type MockCasino = {
  id: string;
  brand: string;
  domain: string;
  ownerEmail: string;
  country: string;
  status: 'live' | 'deploying' | 'paused';
  segments: string[]; // segment ids
};

export const MOCK_CASINOS: MockCasino[] = [
  { id: 'cas-royal', brand: 'Royal Fortune', domain: 'royalfortune.example', ownerEmail: 'owner@royalfortune.example', country: 'MLT', status: 'live', segments: ['seg-active', 'seg-high-velocity', 'seg-crypto'] },
  { id: 'cas-clover', brand: 'Lucky Clover', domain: 'luckyclover.example', ownerEmail: 'dan@example.com', country: 'ROU', status: 'live', segments: ['seg-active'] },
  { id: 'cas-aura', brand: 'Aura Bet', domain: 'aurabet.example', ownerEmail: 'carla@example.com', country: 'BRA', status: 'deploying', segments: ['seg-brazil'] },
  { id: 'cas-gold', brand: 'Gold Rush', domain: 'goldrush.example', ownerEmail: 'owner@goldrush.example', country: 'BRA', status: 'live', segments: ['seg-dormant', 'seg-brazil'] },
  { id: 'cas-neon', brand: 'Neon Jack', domain: 'neonjack.example', ownerEmail: 'hello@neonjack.example', country: 'CAN', status: 'live', segments: ['seg-active', 'seg-crypto'] },
  { id: 'cas-vegas', brand: 'Vegas Flame', domain: 'vegasflame.example', ownerEmail: 'team@vegasflame.example', country: 'MEX', status: 'paused', segments: ['seg-dormant'] },
];

/** A link to the real Leads list filtered to this mock lead's email (it exists in the demo seed). */
export const leadSearchLink = (email: string) => `/leads?q=${encodeURIComponent(email)}`;

/** A link to the real Casinos page filtered to this brand. */
export const casinoSearchLink = (brand: string) => `/casinos?q=${encodeURIComponent(brand)}`;
